import { observer } from "mobx-react-lite";
import { useEffect } from "react";

import { NumberConstraints } from "./NumberConstraints";
import { BuilderProps } from "./types";
import { handleNumberChange } from "./utils";

import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { ParameterValue } from "@/components/ModelParameters/utils";

export const SliderParameterBuilder = observer(({ parameter, isViewing }: BuilderProps) => {
  const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (isNaN(Number(e.target.value))) {
      parameter.updateDefault(0);
    }
  };

  useEffect(() => {
    if (parameter.step !== undefined) {
      parameter.updateStep(Number(parameter.step) || 0);
      parameter.updateMin(Number(parameter.min) || 0);
      parameter.updateMax(Number(parameter.max) || 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parameter.isInt]);

  const handleDefaultChange = (value: string) => {
    const filteredValue = handleNumberChange(value, parameter.isInt);

    if (parameter.min !== undefined && Number(filteredValue) < Number(parameter.min)) {
      parameter.updateDefault(parameter.min);
      return;
    }

    if (parameter.max !== undefined && Number(filteredValue) > Number(parameter.max)) {
      parameter.updateDefault(parameter.max);
      return;
    }

    parameter.updateDefault(filteredValue as ParameterValue);
  };

  const handleStepChange = (value: string) => {
    const positiveValue = value.replace("-", "");

    if (parameter.step !== undefined && Number(positiveValue) > Number(parameter.max)) {
      parameter.updateStep(Number(parameter.max));

      return;
    }

    parameter.updateStep(handleNumberChange(positiveValue, parameter.isInt) as number);
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-6">
        <div>
          <Label htmlFor="stepValue" className="text-sm font-medium">
            Step Value
          </Label>
          <Input
            id="stepValue"
            readOnly={isViewing}
            onBlur={handleOnBlur}
            value={parameter.step as unknown as string}
            onChange={e => handleStepChange(e.target.value)}
            className="mt-2 w-full"
            placeholder="1"
          />
        </div>
        <div>
          <Label htmlFor="defaultValue" className="text-sm font-medium">
            Default Value
          </Label>
          <Input
            id="defaultValue"
            readOnly={isViewing}
            onBlur={handleOnBlur}
            value={parameter.default as unknown as string}
            onChange={e => handleDefaultChange(e.target.value)}
            className="mt-2 w-full"
            placeholder="Default value"
          />
        </div>
      </div>
      <NumberConstraints inputType="number" isViewing={isViewing} parameter={parameter} />
    </div>
  );
});
